import React, { createContext, useContext, useState, useEffect } from 'react';
import { StorageManager } from '../utils/storageManager';

interface LanguageContextType {
  language: 'en' | 'ar';
  toggleLanguage: () => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export { LanguageContext };

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<'en' | 'ar'>(() => {
    // Try to get language from protected storage first
    try {
      return StorageManager.getItem('language', 'en') as 'en' | 'ar';
    } catch {
      // Fallback to direct localStorage
      return (localStorage.getItem('language') as 'en' | 'ar') || 'en';
    }
  });

  useEffect(() => {
    // Store language with high priority protection
    try {
      StorageManager.setItem('language', language, 'high');
    } catch (error) {
      console.error('Failed to store language:', error);
    }
    
    // Apply language to document
    document.documentElement.setAttribute('lang', language);
  }, [language]);

  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'ar' : 'en');
  };

  return (
    <LanguageContext.Provider value={{ language, toggleLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}; 